import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  BuildMarker,
  FeedbackForm,
  Game,
  GameDevStage,
  Milestone,
  Studio,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import { useFeedbackInboxRead } from '../feedback/use-feedback-inbox-read.js';
import { useFeedbackUnreadFlag } from '../feedback/use-feedback-unread-flag.js';
import { useJobActivity, type ActiveBackfillJob } from './use-job-activity.js';
import { fetchFeedbackFormsForGame } from '../games/feedback-forms-data.js';
import {
  fetchBuildMarkersForGame,
  fetchDevStagesForGame,
  fetchGamesForStudio,
  fetchMilestonesForGame,
} from './shell-data.js';

export type ShellContextValue = {
  activeStudio: Studio;
  activeGame: Game | null;
  games: Game[];
  buildMarkers: BuildMarker[];
  activeBuildMarker: BuildMarker | null;
  devStages: GameDevStage[];
  // Resolved from activeBuildMarker.stageId, so the topbar pill and the
  // dropdown header share one source of truth for the current stage's
  // label / colour / tier.
  activeStage: GameDevStage | null;
  milestones: Milestone[];
  activeMilestone: Milestone | null;
  feedbackHasUnread: boolean;
  // Read watermark for the feedback inbox activity panel. Lives on the
  // shell so the sidebar dot and the FeedbackInbox screen share a
  // single Realtime subscription - subscribing twice to the same
  // supabase channel name throws "cannot add postgres_changes
  // callbacks ... after subscribe()".
  feedbackLastReadAt: string | null;
  feedbackIsUnread: (lastActivityAt: string) => boolean;
  feedbackMarkRead: (atMost: string) => Promise<void>;
  loading: boolean;
  refresh: () => Promise<void>;
  // Narrower refreshes so leaf components (Builds page, dev stages
  // section, topbar dropdown) don't refetch everything for one mutation.
  refreshBuilds: () => Promise<void>;
  refreshDevStages: () => Promise<void>;
  // Monotonic counters bumped whenever realtime sees a write to the
  // corresponding table for the active game. Pages that own their own
  // local list (e.g. the Builds management page, which surfaces archived
  // markers) depend on these in a useEffect to refetch themselves when a
  // change lands from another tab or another part of the UI. The shell
  // owns the subscription so we keep one supabase channel per app.
  buildMarkersVersion: number;
  devStagesVersion: number;
  // Feedback Forms list for the active game. Loaded alongside builds /
  // dev stages so the Feedback Settings page never needs its own
  // subscription. The version counter follows the same pattern as
  // builds / dev stages: the form-builder-page reads it to know when an
  // edit landed from another tab without keeping its own Realtime hook.
  feedbackForms: FeedbackForm[];
  refreshFeedbackForms: () => Promise<void>;
  feedbackFormsVersion: number;
  // Playtests realtime. The Playtest Settings list + builder read this
  // counter and refetch when it ticks. Same pattern as feedbackFormsVersion -
  // the shell owns the supabase channel so we don't race on a duplicate
  // channel name. We don't keep the full playtest list on the shell
  // because no other surface needs it (the topbar / sidebar are unaware
  // of playtests for now).
  playtestsVersion: number;
  bumpPlaytestsVersion: () => void;
  // Bumped on every playtest_sessions postgres_changes event for the
  // active game. PR 5's sessions list / detail pages refetch on tick.
  playtestSessionsVersion: number;
  // Insights snapshot realtime. Bumped on every insights:<gameId> broadcast
  // (running / done / dead). The Insights page + channel list read this to
  // refetch; the shell owns the single subscription (use-job-activity.ts) so
  // we never race two subscribers on the same channel name.
  insightsVersion: number;
  // True while an insights refresh is in progress for the active game,
  // spanning collection + the theme/bucket pass (until themes_updated). Drives
  // the inline "Refreshing insights…" banner; hydrated on entry.
  insightsRefreshing: boolean;
  // Discord channel backfill jobs for the active game that are still pending
  // or running. The watched-channels + feedback screens render an inline
  // "Importing history..." banner from this; it survives a reload because the
  // shell hydrates it on mount.
  activeBackfillJobs: ActiveBackfillJob[];
};

export const ShellContext = createContext<ShellContextValue | null>(null);

type ShellProviderProps = {
  studio: Studio;
  gameShortId: string | undefined;
  children: ReactNode;
};

// Per-studio key for the last-selected game's short id. Survives nav into
// non-game routes (e.g. /account) so the topbar + sidebar keep their context.
function lastGameKey(studioId: string): string {
  return `pb:last-game-id:${studioId}`;
}

function readLastGameId(studioId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(lastGameKey(studioId));
  } catch {
    return null;
  }
}

function writeLastGameId(studioId: string, gameShortId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(lastGameKey(studioId), gameShortId);
  } catch {
    // localStorage can throw in private mode / quota; non-fatal.
  }
}

export function ShellProvider({
  studio,
  gameShortId,
  children,
}: ShellProviderProps): JSX.Element {
  const [games, setGames] = useState<Game[]>([]);
  const [buildMarkers, setBuildMarkers] = useState<BuildMarker[]>([]);
  const [devStages, setDevStages] = useState<GameDevStage[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [feedbackForms, setFeedbackForms] = useState<FeedbackForm[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [buildMarkersVersion, setBuildMarkersVersion] = useState(0);
  const [devStagesVersion, setDevStagesVersion] = useState(0);
  const [feedbackFormsVersion, setFeedbackFormsVersion] = useState(0);
  const [playtestsVersion, setPlaytestsVersion] = useState(0);
  const bumpPlaytestsVersion = useCallback(() => {
    setPlaytestsVersion((v) => v + 1);
  }, []);
  const [playtestSessionsVersion, setPlaytestSessionsVersion] = useState(0);
  const [lastGameId, setLastGameId] = useState<string | null>(() =>
    readLastGameId(studio.id),
  );

  // When the URL puts us on a game, remember it for later non-game routes.
  useEffect(() => {
    if (gameShortId !== undefined && gameShortId !== lastGameId) {
      setLastGameId(gameShortId);
      writeLastGameId(studio.id, gameShortId);
    }
  }, [gameShortId, lastGameId, studio.id]);

  // Reload the persisted id when the active studio changes.
  useEffect(() => {
    setLastGameId(readLastGameId(studio.id));
  }, [studio.id]);

  // Resolve activeGame from the URL first, falling back to the last-selected
  // short id so /account et al. keep showing the previously-active game.
  const activeGame = useMemo<Game | null>(() => {
    const candidate = gameShortId ?? lastGameId;
    if (candidate === null || candidate === undefined) return null;
    return games.find((g) => g.shortId === candidate) ?? null;
  }, [games, gameShortId, lastGameId]);

  const activeBuildMarker = useMemo<BuildMarker | null>(
    () => buildMarkers[0] ?? null,
    [buildMarkers],
  );
  const activeStage = useMemo<GameDevStage | null>(() => {
    if (activeBuildMarker === null) return null;
    return devStages.find((s) => s.id === activeBuildMarker.stageId) ?? null;
  }, [activeBuildMarker, devStages]);
  const activeMilestone = useMemo<Milestone | null>(
    () => milestones[0] ?? null,
    [milestones],
  );

  const refreshBuilds = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setBuildMarkers([]);
      return;
    }
    const next = await fetchBuildMarkersForGame(activeGame.id);
    setBuildMarkers(next);
  }, [activeGame]);

  const refreshDevStages = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setDevStages([]);
      return;
    }
    const next = await fetchDevStagesForGame(activeGame.id);
    setDevStages(next);
  }, [activeGame]);

  const refreshFeedbackForms = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setFeedbackForms([]);
      return;
    }
    // Shell list excludes archived; the Forms page fetches its own list
    // with includeArchived: true so the show-archived toggle has rows to
    // surface. The submission count returned alongside isn't needed here.
    const next = await fetchFeedbackFormsForGame(activeGame.id);
    setFeedbackForms(next.map((row) => row.form));
    // Page-owned lists (FormsSection) reload off feedbackFormsVersion. We
    // bump it explicitly here so callers that mutate via RPC stay synced
    // even when realtime can't carry the event - notably DELETE, which
    // Postgres Realtime drops when a row filter is set because the
    // post-image needed to evaluate the filter does not exist.
    setFeedbackFormsVersion((v) => v + 1);
  }, [activeGame]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    const nextGames = await fetchGamesForStudio(studio.id);
    setGames(nextGames);

    // Use the URL's game first, fall back to the persisted one so the topbar
    // build / milestone selectors stay populated on non-game routes.
    const candidateId = gameShortId ?? lastGameId;
    const matched = candidateId !== null && candidateId !== undefined
      ? nextGames.find((g) => g.shortId === candidateId) ?? null
      : null;

    if (matched !== null) {
      const [nextBuilds, nextStages, nextMilestones, nextForms] = await Promise.all([
        fetchBuildMarkersForGame(matched.id),
        fetchDevStagesForGame(matched.id),
        fetchMilestonesForGame(matched.id),
        fetchFeedbackFormsForGame(matched.id),
      ]);
      setBuildMarkers(nextBuilds);
      setDevStages(nextStages);
      setMilestones(nextMilestones);
      setFeedbackForms(nextForms.map((row) => row.form));
    } else {
      setBuildMarkers([]);
      setDevStages([]);
      setMilestones([]);
      setFeedbackForms([]);
    }
    setLoading(false);
  }, [studio.id, gameShortId, lastGameId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Feedback unread badge. Two pieces:
  //   1. The per-game "last read" watermark (shared across studio members)
  //   2. The latest watched-channel posted_at, streamed via Realtime
  // The dot is on whenever the latest posted_at is past the watermark.
  // Both hooks no-op when there is no active game. The read-state
  // hook is intentionally singular at this level - the FeedbackInbox
  // screen consumes the same values via ShellContext rather than
  // mounting its own copy, otherwise both subscriptions race for the
  // same channel name.
  const { lastReadAt, isUnread, markRead } = useFeedbackInboxRead(
    activeGame?.id ?? null,
  );
  const feedbackHasUnread = useFeedbackUnreadFlag(
    activeGame?.id ?? null,
    lastReadAt,
  );

  // Background-job activity (insights refresh + channel backfill). Owns the
  // insights:<gameId> + backfill-jobs:<gameId> subscriptions and raises
  // toasts; exposes insightsVersion + activeBackfillJobs below.
  const { insightsVersion, insightsRefreshing, activeBackfillJobs } = useJobActivity(
    activeGame?.id ?? null,
  );

  // Build management realtime. One channel per active game, owned here
  // so we never end up with two hook instances racing on the same
  // supabase channel name (per the shared-hook lesson learned in the
  // feedback inbox work). Each event:
  //   - bumps buildMarkers / devStages versions, which leaf pages depend
  //     on to refetch their own local lists,
  //   - and refreshes the shell-owned active list so the topbar pill
  //     reflects the change immediately without waiting on the page.
  // We keep the payload-driven path simple: any change triggers a
  // refetch. Build markers are infrequent enough that the round trip
  // cost is negligible compared to the simplicity gain.
  const refreshBuildsRef = useRef(refreshBuilds);
  const refreshDevStagesRef = useRef(refreshDevStages);
  const refreshFeedbackFormsRef = useRef(refreshFeedbackForms);
  refreshBuildsRef.current = refreshBuilds;
  refreshDevStagesRef.current = refreshDevStages;
  refreshFeedbackFormsRef.current = refreshFeedbackForms;

  useEffect(() => {
    if (activeGame === null) return;
    const gameId = activeGame.id;
    // Channel name retained as `build-management:` for compatibility with
    // existing subscribers; it now also carries feedback_forms changes for
    // the same game. The single channel keeps us under the "one supabase
    // channel name per app" rule we hit on the inbox work.
    const channel = supabase
      .channel(`build-management:${gameId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'build_markers',
          filter: `game_id=eq.${gameId}`,
        },
        () => {
          setBuildMarkersVersion((v) => v + 1);
          void refreshBuildsRef.current();
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_dev_stages',
          filter: `game_id=eq.${gameId}`,
        },
        () => {
          setDevStagesVersion((v) => v + 1);
          void refreshDevStagesRef.current();
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'feedback_forms',
          filter: `game_id=eq.${gameId}`,
        },
        () => {
          setFeedbackFormsVersion((v) => v + 1);
          void refreshFeedbackFormsRef.current();
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'playtests',
          filter: `game_id=eq.${gameId}`,
        },
        () => {
          setPlaytestsVersion((v) => v + 1);
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'playtest_sessions',
          filter: `game_id=eq.${gameId}`,
        },
        () => {
          setPlaytestSessionsVersion((v) => v + 1);
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [activeGame]);

  const value = useMemo<ShellContextValue>(
    () => ({
      activeStudio: studio,
      activeGame,
      games,
      buildMarkers,
      activeBuildMarker,
      devStages,
      activeStage,
      milestones,
      activeMilestone,
      feedbackHasUnread,
      feedbackLastReadAt: lastReadAt,
      feedbackIsUnread: isUnread,
      feedbackMarkRead: markRead,
      loading,
      refresh: load,
      refreshBuilds,
      refreshDevStages,
      buildMarkersVersion,
      devStagesVersion,
      feedbackForms,
      refreshFeedbackForms,
      feedbackFormsVersion,
      playtestsVersion,
      bumpPlaytestsVersion,
      playtestSessionsVersion,
      insightsVersion,
      insightsRefreshing,
      activeBackfillJobs,
    }),
    [
      studio,
      activeGame,
      games,
      buildMarkers,
      activeBuildMarker,
      devStages,
      activeStage,
      milestones,
      activeMilestone,
      feedbackHasUnread,
      lastReadAt,
      isUnread,
      markRead,
      loading,
      load,
      refreshBuilds,
      refreshDevStages,
      buildMarkersVersion,
      devStagesVersion,
      feedbackForms,
      refreshFeedbackForms,
      feedbackFormsVersion,
      playtestsVersion,
      bumpPlaytestsVersion,
      playtestSessionsVersion,
      insightsVersion,
      insightsRefreshing,
      activeBackfillJobs,
    ],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}
